import { X } from 'lucide-react';
import { type ReactNode, useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../lib/utils';
import { Input } from './ui/input';

export interface PickerOption {
  value: string;
  label: string;
  hint?: string;
  icon?: ReactNode;
}

/**
 * A full-screen picker for phones: a title, an optional search box, and the options as one list
 * group. Escape or the close button cancels. Used for the category and account rows of the Add
 * screen.
 */
export function PickerSheet({
  title,
  options,
  selected,
  onPick,
  onClose,
  search,
  footer,
  empty,
}: {
  title: string;
  options: readonly PickerOption[];
  selected?: string;
  onPick: (value: string) => void;
  onClose: () => void;
  search?: { value: string; onChange: (value: string) => void; label: string };
  footer?: ReactNode;
  empty?: string;
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const searchRef = useRef<HTMLInputElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    (searchRef.current ?? closeRef.current)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-30 overflow-y-auto bg-bg text-fg"
    >
      <div className="mx-auto flex max-w-[480px] flex-col gap-4 px-5 pt-6 pb-[calc(2rem+env(safe-area-inset-bottom))]">
        <div className="flex items-center justify-between gap-3">
          <h2 id={titleId} className="text-[22px] font-semibold">
            {title}
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="flex size-11 items-center justify-center rounded-2xl hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-accent"
          >
            <X aria-hidden="true" className="size-[22px]" strokeWidth={2} />
          </button>
        </div>
        {search && (
          <Input
            ref={searchRef}
            type="search"
            aria-label={search.label}
            placeholder={search.label}
            value={search.value}
            onChange={(e) => search.onChange(e.target.value)}
            autoComplete="off"
          />
        )}
        {options.length > 0 ? (
          <ul className="flex flex-col divide-y divide-line overflow-hidden rounded-[20px] border border-line bg-surface">
            {options.map((o) => (
              <li key={o.value}>
                <button
                  type="button"
                  aria-pressed={selected === o.value}
                  onClick={() => onPick(o.value)}
                  className={cn(
                    'flex min-h-14 w-full items-center gap-3 px-4 py-3 text-start hover:bg-surface-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent',
                    selected === o.value && 'bg-surface-2',
                  )}
                >
                  {o.icon}
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-[15px] font-medium">{o.label}</span>
                    {o.hint && <span className="truncate text-xs text-muted">{o.hint}</span>}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          empty && <p className="px-1 text-muted">{empty}</p>
        )}
        {footer}
      </div>
    </div>
  );
}
